import { useEffect, useState } from "react";
import { api, type TrashData } from "../lib/api.js";

/** Corbeille (§6.7) : éléments en suppression douce, restauration, et purge manuelle. */
export function TrashPanel({ onChanged }: { onChanged: () => void }) {
  const [data, setData] = useState<TrashData | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .trash()
      .then((d) => !cancelled && setData(d))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refresh = () => setTick((t) => t + 1);
  const restore = async (type: "capture" | "standalone" | "summary", id: string) => {
    setBusy(true);
    await api.restoreTrash(type, id).catch(() => undefined);
    setBusy(false);
    refresh();
    onChanged();
  };
  const purge = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.purge();
      setMsg(`Purge effectuée : ${r.purgedAudioSegments} audio, ${r.purgedTrashItems} élément(s).`);
    } catch {
      setMsg("Purge impossible.");
    }
    setBusy(false);
    refresh();
    onChanged();
  };

  const empty =
    data &&
    data.captures.length === 0 &&
    data.standaloneNotes.length === 0 &&
    data.summaries.length === 0;

  return (
    <div className="set-card">
      {!data ? (
        <div className="listspinner"><div className="spinner" /></div>
      ) : empty ? (
        <div className="set-desc" style={{ padding: "10px 0" }}>La corbeille est vide.</div>
      ) : (
        <>
          {data.captures.length > 0 && <div className="trash-group">Captures</div>}
          {data.captures.map((c) => (
            <div key={c.id} className="trash-row">
              <span className="set-label">{c.mode} · {c.kind}</span>
              <button className="trash-restore" disabled={busy} onClick={() => restore("capture", c.id)}>Restaurer</button>
            </div>
          ))}
          {data.standaloneNotes.length > 0 && <div className="trash-group">Notes</div>}
          {data.standaloneNotes.map((n) => (
            <div key={n.id} className="trash-row">
              <span className="set-label">{n.title ?? "Note"}</span>
              <button className="trash-restore" disabled={busy} onClick={() => restore("standalone", n.id)}>Restaurer</button>
            </div>
          ))}
          {data.summaries.length > 0 && <div className="trash-group">Résumés</div>}
          {data.summaries.map((s) => (
            <div key={s.id} className="trash-row">
              <span className="set-label">{s.targetType} · résumé {s.seq}</span>
              <button className="trash-restore" disabled={busy} onClick={() => restore("summary", s.id)}>Restaurer</button>
            </div>
          ))}
        </>
      )}
      <div className="set-divline" />
      <div className="set-row">
        <span className="set-label-wrap">
          <span className="set-label">Purger maintenant</span>
          <span className="set-desc">Supprime définitivement l'audio périmé et la corbeille au-delà des délais.</span>
        </span>
        <button className="trash-restore" disabled={busy} onClick={purge}>Purger</button>
      </div>
      {msg && <div className="editwin-flash">{msg}</div>}
    </div>
  );
}
